import { useMutation } from "@apollo/client";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { gql } from "generated-graphql";
import {
  ActivityStatus,
  ActivityUserInput as ActivityUserInputGql,
  ActivityUserInputType,
} from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useRef, useState } from "react";

import { useAlerts } from "../../../../../components/Alerts/AlertProvider";
import { isPlainNumber } from "./activityInputValidation";
import { LobFeeInputs } from "./LobFeeInputs";

/**
 * Generic type for activities that have inputs - works for both
 * Fulfillment (TierIIReport) and Cohort workflows.
 */
type ActivityWithInputs = {
  status: ActivityStatus;
  inputs?: Array<ActivityUserInputGql | null> | null;
};

type Props = {
  activity: ActivityWithInputs;
};

const UPDATE_ACTIVITY_INPUT_MUTATION = gql(`
  mutation SetValueOnActivityUserInput($id: ID!, $value: String!) {
    setValueOnActivityUserInput(id: $id, value: $value) {
      id
      value
}}`);

export const ActivityInputs = ({ activity }: Props) => {
  const inputs = activity.inputs;
  if (!inputs || inputs?.length === 0) {
    return null;
  }
  const disabled = [ActivityStatus.Completed, ActivityStatus.StaffCompleted].includes(
    activity.status
  );

  const feeInput = inputs?.find((i) => i?.shortName === "lobFee") ?? null;
  const noFeeInput = inputs?.find((i) => i?.shortName === "noLobFee") ?? null;

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Divider />
      <Typography variant="h6">User Inputs</Typography>
      {feeInput && noFeeInput && (
        <LobFeeInputs feeInput={feeInput} noFeeInput={noFeeInput} disabled={disabled} />
      )}
      {inputs.map((input, index) => {
        if (!input) {
          return null;
        }
        // The lobFee/noLobFee pair is rendered together by LobFeeInputs above.
        if (input.shortName === "lobFee" || input.shortName === "noLobFee") {
          return null;
        }
        return <ActivityUserInput key={index} input={input} disabled={disabled} />;
      })}
      <Divider />
    </Box>
  );
};

const ActivityUserInput = ({
  input,
  disabled,
}: {
  input: ActivityUserInputGql;
  disabled: boolean;
}) => {
  const { id, label, required, type, value: initialValue } = input;
  const alerts = useAlerts();
  const [setInput, { loading }] = useMutation(UPDATE_ACTIVITY_INPUT_MUTATION);
  const [value, setValue] = useState(initialValue ?? "");
  const [debouncedInput] = useDebounce(value, 1000);

  // Sequential write queue + generation counter (see LobFeeInputs for the full
  // rationale): writes dispatch in user-intent order, and a failed write only
  // reverts local state when nothing newer (edit or write) has happened since.
  const writeQueueRef = useRef<Promise<void>>(Promise.resolve());
  const writeGenRef = useRef(0);

  const valueError = type === ActivityUserInputType.Number && !!value && !isPlainNumber(value);

  useEffect(() => {
    if (debouncedInput === (initialValue ?? "")) {
      return;
    }
    // Never persist a number the field is flagging as invalid: downstream parsing
    // strips commas, so e.g. "12,50" would silently be read as 1250.
    if (type === ActivityUserInputType.Number && debouncedInput && !isPlainNumber(debouncedInput)) {
      return;
    }
    const gen = ++writeGenRef.current;
    writeQueueRef.current = writeQueueRef.current.then(async () => {
      try {
        await setInput({ variables: { id, value: debouncedInput } });
      } catch (e) {
        // Restore the last server-accepted value so the UI never claims a value
        // persisted when the write was rejected - unless the user has already
        // moved on (newer edit/write), in which case reverting would clobber
        // their current intent.
        if (gen === writeGenRef.current) {
          setValue(initialValue ?? "");
        }
        alerts.error(`Failed to save "${label}".`, e);
      }
    });
  }, [debouncedInput, id, initialValue, setInput, type, label, alerts]);

  switch (type) {
    case ActivityUserInputType.Boolean:
      return (
        <Stack direction="row" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                required={required}
                disabled={disabled}
                checked={value === "true"}
                onChange={(e) => {
                  writeGenRef.current++;
                  setValue(e.target.checked ? "true" : "false");
                }}
              />
            }
            label={label}
          />
          {loading && <CircularProgress size={20} />}
        </Stack>
      );
    case ActivityUserInputType.Number:
      return (
        <TextField
          sx={{ my: 1 }}
          disabled={disabled}
          required={required}
          label={label}
          value={value}
          onChange={(e) => {
            writeGenRef.current++;
            setValue(e.target.value);
          }}
          error={valueError}
          helperText={
            valueError ? "Value must be a plain number without commas, spaces, or symbols." : ""
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? <CircularProgress size={20} /> : <Box sx={{ width: 20 }} />}
              </InputAdornment>
            ),
          }}
        />
      );
    default:
      return null;
  }
};
